'use client'

import { cn } from '@/lib/utils'
import React from 'react'
import { useFormStatus } from 'react-dom'

import {
  Button,
  KeyChip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@/components/ui'
import { FloatingKeyboard } from '@/components/ui/keyboard'

interface CardValidationInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  isFirstFourRequired?: boolean
  isLastFourRequired?: boolean
}

export const CardValidationInput = React.forwardRef<
  HTMLInputElement,
  CardValidationInputProps
>(({ className, isFirstFourRequired, isLastFourRequired }, ref) => {
  const { pending } = useFormStatus()
  const firstFourRef = React.useRef<HTMLInputElement>(null)
  const lastFourRef = React.useRef<HTMLInputElement>(null)
  const showVirtualKeyboard = localStorage?.getItem('showVirtualKeyboard') === 'true'

  return (
    <div
      className={cn(
        'flex h-[52px] w-full items-center gap-1 overflow-hidden rounded-lg border border-gray-100 bg-white px-1 py-1 ring-gray-700 focus-within:bg-white focus-within:ring-2 dark:border-gray-700 dark:bg-gray-900',
        className,
      )}
      ref={ref}
    >
      {isFirstFourRequired &&
        (showVirtualKeyboard ? (
          <Popover>
            <PopoverTrigger asChild>
              <input
                type="text"
                placeholder="0000"
                className="placeholder:text-normal h-full w-full bg-transparent px-2 text-center text-sm font-medium placeholder-gray-400 focus-visible:outline-none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={4}
                name="first-four-digits"
                ref={firstFourRef}
                onChange={(e) => {
                  if (e.target.value.length === e.target.maxLength) {
                    lastFourRef?.current?.focus()
                  }
                }}
              />
            </PopoverTrigger>
            <PopoverContent>
              <FloatingKeyboard
                type="number"
                isNumberOnly
                onKeyPress={(value) => {
                  if (!firstFourRef.current) return

                  if (value === '⌫') {
                    const newValue = firstFourRef.current.value.slice(0, -1)
                    firstFourRef.current.value = newValue
                  } else if (value === 'clear') {
                    firstFourRef.current.value = ''
                  } else if (firstFourRef.current.value.length < 4) {
                    firstFourRef.current.value += value
                    if (firstFourRef.current.value.length === 4) {
                      lastFourRef?.current?.focus()
                    }
                  }
                }}
              />
            </PopoverContent>
          </Popover>
        ) : (
          <input
            type="text"
            placeholder="0000"
            className="placeholder:text-normal h-full w-full bg-transparent px-2 text-center text-sm font-medium placeholder-gray-400 focus-visible:outline-none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={4}
            name="first-four-digits"
            ref={firstFourRef}
            onChange={(e) => {
              if (e.target.value.length === e.target.maxLength) {
                lastFourRef?.current?.focus()
              }
            }}
          />
        ))}

      {isFirstFourRequired && (
        <Separator
          className="h-full w-[1px] shrink-0 bg-gray-100"
          orientation="vertical"
        />
      )}

      {isLastFourRequired &&
        (showVirtualKeyboard ? (
          <Popover>
            <PopoverTrigger asChild>
              <input
                type="text"
                placeholder="0000"
                className="placeholder:text-normal h-full w-full bg-transparent px-2 pl-4 text-center text-sm font-medium placeholder-gray-400 focus-visible:outline-none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={4}
                name="last-four-digits"
                ref={lastFourRef}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && e.currentTarget.value === '') {
                    firstFourRef?.current?.focus()
                  }
                }}
              />
            </PopoverTrigger>
            <PopoverContent className="z-[1000]">
              <FloatingKeyboard
                type="number"
                isNumberOnly
                onKeyPress={(value) => {
                  if (!lastFourRef.current) return

                  if (value === '⌫') {
                    const newValue = lastFourRef.current.value.slice(0, -1)
                    if (lastFourRef.current.value === '') {
                      firstFourRef?.current?.focus()
                    } else {
                      lastFourRef.current.value = newValue
                    }
                  } else if (value === 'clear') {
                    lastFourRef.current.value = ''
                  } else if (lastFourRef.current.value.length < 4) {
                    lastFourRef.current.value += value
                  }
                }}
              />
            </PopoverContent>
          </Popover>
        ) : (
          <input
            type="text"
            placeholder="0000"
            className="placeholder:text-normal h-full w-full bg-transparent px-2 pl-4 text-center text-sm font-medium placeholder-gray-400 focus-visible:outline-none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={4}
            name="last-four-digits"
            ref={lastFourRef}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && e.currentTarget.value === '') {
                firstFourRef?.current?.focus()
              }
            }}
          />
        ))}

      <Button
        disabled={pending}
        aria-disabled={pending}
        className="h-full gap-2 rounded-md pl-4"
        tabIndex={-1}
        type="submit"
      >
        <span>{pending ? 'Submitting' : 'Submit'}</span>
        <KeyChip variant="lighter" shortcut="enter" className="h-full" />
      </Button>
    </div>
  )
})

CardValidationInput.displayName = 'CardValidationInput'
